/**
 * URL handoffs for primer continuity traversal (call → replay → verify → witness).
 */

export const VERIFY_SURFACE_URL = "https://verify.xo-vault.com/vault/verify.html";
export const MESSAGE_BOTTLE_WITNESS_RECORD_PATH = "/records/message-bottle-v2";
/** Witness evolution capstone for traversal verify handoff (Genesis → Orion → Lyra). */
export const TRAVERSAL_WITNESS_LINEAGE_PATH = "/posts/message-bottle-v2-lyra/";

export type CallMomentHandoff = {
  roomId: string;
  callId: string;
  snapshotId: string;
  agreementHash: string;
};

export function buildContinuityReplayUrl(input: {
  roomId: string;
  callId: string;
  snapshotId: string;
}): string {
  return `/continuity-replay?room=${encodeURIComponent(input.roomId)}&call=${encodeURIComponent(input.callId)}&snapshot=${encodeURIComponent(input.snapshotId)}`;
}

/** Prefills verify with agreement fingerprint + witness lineage entry (honest: inspect registry, not fake proof). */
export function buildVerifyHandoffFromCallMoment(input: CallMomentHandoff & { witnessPath?: string }): string {
  const url = new URL(VERIFY_SURFACE_URL);
  url.searchParams.set("traverse", "1");
  url.searchParams.set("sha", input.agreementHash);
  url.searchParams.set("source", "call-continuity");
  url.searchParams.set("snapshot", input.snapshotId);
  url.searchParams.set("call", input.callId);
  url.searchParams.set("room", input.roomId);
  const witness = input.witnessPath ?? TRAVERSAL_WITNESS_LINEAGE_PATH;
  url.searchParams.set("witness", witness);
  return url.href;
}

export function buildWitnessRecordUrl(): string {
  return MESSAGE_BOTTLE_WITNESS_RECORD_PATH;
}

export const CALL_TO_PRIMER_HANDOFF_FROM = "call-sealed";
export const PRIMER_CANONICAL_2M_ROUTE = "canonical.2m.s1";

/** Tier-B Primer continuation after a sealed Call traversal demo (canonical.2m.s1 entry). */
export function buildCallToPrimerDemoHandoffUrl(input?: {
  callId?: string;
  roomId?: string;
  snapshotId?: string;
}): string {
  const params = new URLSearchParams();
  params.set("from", CALL_TO_PRIMER_HANDOFF_FROM);
  params.set("route", PRIMER_CANONICAL_2M_ROUTE);
  if (input?.callId?.trim()) params.set("call", input.callId.trim());
  if (input?.roomId?.trim()) params.set("room", input.roomId.trim());
  if (input?.snapshotId?.trim()) params.set("snapshot", input.snapshotId.trim());
  const query = params.toString();
  return query ? `/primer?${query}` : "/primer";
}

export function isCallSealedPrimerEntry(search: string): boolean {
  const params = new URLSearchParams(search.startsWith("?") ? search : search ? `?${search}` : "");
  return params.get("from") === CALL_TO_PRIMER_HANDOFF_FROM;
}
